import { useMemo } from 'react';

import { useQueries, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { API, endpoints } from '@/api/metrics';
import {
  type DerivedMetric,
  getTableMeta,
  mergeFilters,
  resolveRequestSource,
} from '@/modules/analytics/constants/metricsCatalog';
import { DEFAULT_RANGE, resolveRange } from '@/modules/analytics/constants/timeRange';
import { useMultiGroupLabelResolver } from '@/modules/analytics/hooks/useGroupLabelResolver';
import { formatDurationFromSeconds } from '@/modules/analytics/utils/formatDuration';
import { selectAccountId } from '@/redux/session/selectors';
import type {
  MetricsQueryCell,
  MetricsQueryRequest,
  MetricsQueryResponse,
  MetricsQueryRow,
} from '@/shared/types/metricsQuery';
import type { MetricRange, ReportMetric } from '@/shared/types/reports';

type UseReportMetricOptions = { enabled?: boolean; staleTime?: number };

const buildKey = (accountId: string | undefined, request: MetricsQueryRequest | null) =>
  [endpoints.query, accountId, request] as const;

export const useReportMetric = (
  metric: MetricsQueryRequest | null | undefined,
  options: UseReportMetricOptions = {}
) => {
  const accountId = useSelector(selectAccountId);
  const { enabled = true, staleTime } = options;

  return useQuery<MetricsQueryResponse>({
    queryKey: buildKey(accountId, metric ?? null),
    queryFn: () => API.query(metric as MetricsQueryRequest),
    enabled: !!accountId && !!metric && enabled,
    staleTime,
  });
};

export const useReportMetrics = (
  metrics: MetricsQueryRequest[] | undefined,
  options: UseReportMetricOptions = {}
) => {
  const accountId = useSelector(selectAccountId);
  const { enabled = true, staleTime } = options;

  return useQueries({
    queries: (metrics ?? []).map((metric) => ({
      queryKey: buildKey(accountId, metric),
      queryFn: () => API.query(metric),
      enabled: !!accountId && enabled,
      staleTime,
    })),
  });
};

export type ChartDataPoint = { date: string; value: number };
export type ChartSeries = { key: string; label: string; data: ChartDataPoint[] };

const TIME_BUCKET_KEYS = ['bucket', 'time_bucket', 'date'] as const;

const findTimeBucketKey = (row: MetricsQueryRow): string | undefined => {
  for (const candidate of TIME_BUCKET_KEYS) {
    if (candidate in row) {
      return candidate;
    }
  }
  return undefined;
};

export const toNumber = (raw: MetricsQueryCell): number | null => {
  if (typeof raw === 'number') {
    return raw;
  }
  if (typeof raw === 'string') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const formatGroupKey = (raw: MetricsQueryCell): string => (raw == null ? '∅' : String(raw));

export type ValueFormatter = (value: number) => string;

const identityFormatter: ValueFormatter = (value) => value.toLocaleString();

export const buildValueFormatter = (derived: DerivedMetric | undefined): ValueFormatter => {
  if (!derived) {
    return identityFormatter;
  }
  if (derived.format === 'percent') {
    return (value) => `${value}%`;
  }
  if (derived.format === 'score_5') {
    return (value) => `${value.toFixed(1)}/5`;
  }
  if (derived.format === 'duration_seconds') {
    // Sub-minute values keep one decimal so response-time scatter (0.4s vs 0.8s)
    // doesn't collapse into the same tick. Anything >=60s is `Nm Ns` via the
    // shared helper, matching the overview tiles and delta formatters.
    return (value) => formatDurationFromSeconds(value, 1);
  }
  return identityFormatter;
};

// Returns null when the bucket can't produce a meaningful derived value (missing
// numerator/denominator, or denominator is zero — division by zero). The caller
// skips these so the chart shows isolated dots for sparse data rather than
// interpolating across gaps.
export const computeDerivedValue = (
  row: MetricsQueryRow,
  derived: DerivedMetric
): number | null => {
  const numerator = toNumber(row[derived.numerator]);
  const denominator = toNumber(row[derived.denominator]);
  if (numerator == null || denominator == null || denominator === 0) {
    return null;
  }
  const ratio =
    derived.kind === 'ratio_complement' ? 1 - numerator / denominator : numerator / denominator;
  // `percent` rounds to an integer so the chart's Y-axis ticks read as whole
  // percentages. Other formats (e.g. `score_5`) keep the raw decimal so the
  // formatter can render `4.3/5` without losing precision.
  return derived.format === 'percent' ? Math.round(ratio * 100) : ratio;
};

// Matches ROW_KEY_SEPARATOR in useReportMetricTable.ts. Both modules join
// per-column group keys into a single composite key with this delimiter so
// the chart's `series.key` and the table's `rowKey` line up — this is what
// lets the parent share a single `colorByKey` Map across both views.
const SERIES_KEY_SEPARATOR = '__';

const ALL_SERIES_KEY = '__all__';

type PivotedSeries = { key: string; groupKeys: string[]; data: ChartDataPoint[] };

const pivotRows = (
  rows: MetricsQueryRow[],
  groupColumns: string[],
  valueAlias: string,
  derived: DerivedMetric | undefined
): PivotedSeries[] => {
  if (rows.length === 0) {
    return [];
  }
  const timeKey = findTimeBucketKey(rows[0]);
  if (!timeKey) {
    return [];
  }
  const seriesMap = new Map<string, PivotedSeries>();
  rows.forEach((row) => {
    const date = row[timeKey];
    const value = derived ? computeDerivedValue(row, derived) : toNumber(row[valueAlias]);
    if (typeof date !== 'string' || value == null) {
      return;
    }
    const groupKeys =
      groupColumns.length === 0 ? [] : groupColumns.map((c) => formatGroupKey(row[c]));
    const seriesKey =
      groupColumns.length === 0 ? ALL_SERIES_KEY : groupKeys.join(SERIES_KEY_SEPARATOR);
    const existing = seriesMap.get(seriesKey) ?? { key: seriesKey, groupKeys, data: [] };
    existing.data.push({ date, value });
    seriesMap.set(seriesKey, existing);
  });
  return Array.from(seriesMap.values()).map((s) => ({
    ...s,
    data: s.data.sort((a, b) => a.date.localeCompare(b.date)),
  }));
};

const granularityForRange = (range: MetricRange): string => {
  if (range.kind === 'relative' && range.period === 'today') {
    return '1h';
  }
  return '1d';
};

const buildSeriesRequest = (metric: ReportMetric): MetricsQueryRequest => {
  const range = metric.range ?? DEFAULT_RANGE;
  const { timeRange } = resolveRange(range);
  // Heal a stale snapshot before sending: a re-pointed table, re-pointed
  // aggregations, or a persisted groupBy column that's no longer groupable.
  // The resolved catalog entry rides along for the identity filter below.
  const source = resolveRequestSource(metric.query, metric.metricId);
  // Merge the catalog's identity filter (e.g. `insight_name eq 'Promise_to_pay'`
  // for custom_promise_to_pay, `event eq 'message:brain_send'` for the response
  // time metric) into the outgoing request. Not persisted on save so the catalog
  // stays source-of-truth and legacy reports get the fix without a migration.
  const filter = mergeFilters(source.metric?.defaultFilter, metric.query.filter);
  const base: MetricsQueryRequest = {
    ...metric.query,
    table: source.table,
    aggregations: source.aggregations,
    ...(source.groupBy ? { groupBy: source.groupBy } : {}),
    timeRange,
    ...(filter ? { filter } : {}),
  };
  const tableMeta = getTableMeta(base.table);
  if (!tableMeta) {
    return base;
  }
  if (tableMeta.type === 'raw') {
    return { ...base, granularity: granularityForRange(range) };
  }
  const existingGroupBy = base.groupBy ?? [];
  if (existingGroupBy.includes(tableMeta.timeColumn)) {
    return base;
  }
  return { ...base, groupBy: [tableMeta.timeColumn, ...existingGroupBy] };
};

export const useReportMetricSeries = (
  metric: ReportMetric | null | undefined,
  options: UseReportMetricOptions = {}
) => {
  const request = useMemo(() => (metric ? buildSeriesRequest(metric) : null), [metric]);
  const query = useReportMetric(request, options);
  // Resolve once for display concerns. groupColumns must mirror the healed
  // request's grouping (minus the time bucket the builder prepends) so pivoting
  // matches the response rows, not the persisted snapshot.
  const source = useMemo(
    () => (metric ? resolveRequestSource(metric.query, metric.metricId) : undefined),
    [metric]
  );
  const groupColumns = useMemo(() => source?.groupBy ?? [], [source]);
  const resolveLabel = useMultiGroupLabelResolver(groupColumns, query.data?.data);
  const catalogMetric = source?.metric;
  const derived = catalogMetric?.derived;
  const valueFormatter = useMemo(() => buildValueFormatter(derived), [derived]);

  const series = useMemo<ChartSeries[]>(() => {
    if (!metric || !query.data) {
      return [];
    }
    // Read the alias from the healed request, not the persisted snapshot — after
    // a heal the response rows are keyed by the catalog's aliases.
    const valueAlias = request?.aggregations?.[0]?.alias ?? 'value';
    const pivoted = pivotRows(query.data.data, groupColumns, valueAlias, derived);
    // No groupBy: use the metric's human label, not the raw alias ("not_contained").
    const singleSeriesLabel = catalogMetric?.valueLabel ?? catalogMetric?.label ?? valueAlias;
    return pivoted.map((s) => {
      if (s.key === ALL_SERIES_KEY) {
        return { key: s.key, label: singleSeriesLabel, data: s.data };
      }
      const label = s.groupKeys.map((key, idx) => resolveLabel(groupColumns[idx], key)).join(' · ');
      return { key: s.key, label, data: s.data };
    });
  }, [metric, request, query.data, groupColumns, resolveLabel, derived, catalogMetric]);

  return { ...query, series, valueFormatter };
};
